import { faClone, faPencil, faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import { Flex } from '@mantine/core';
import type { IObservableArray } from 'mobx';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Campaign, DisplayCreative } from '@feathr/blackbox';
import type { ICreativeCardMenuItem, IUsePoofResult } from '@feathr/components';
import { CreativeCard, PoofGridItem } from '@feathr/components';
import AddCreativeButton from '@feathr/extender/components/Creatives/AddCreativeButton';
import CreativeDefaultSettings from '@feathr/extender/components/Creatives/CreativeDefaultSettings';
import CreativeModal from '@feathr/extender/components/Creatives/CreativeModal';
import type { IThumbnailCarouselItem } from '@feathr/extender/components/Creatives/CreativeModal/ThumbnailCarousel';
import { CreativeRequirementsCard } from '@feathr/extender/components/Creatives/CreativeRequirementsCard';
import {
  useDuplicateCreative,
  useRemoveCreative,
} from '@feathr/extender/components/Creatives/Creatives.hooks';
import CreativesGrid from '@feathr/extender/components/Creatives/CreativesGrid';
import { useToggle } from '@feathr/hooks';

import * as styles from './AdWizardCreativesStepV2.css';

interface IProps {
  campaign: Campaign;
  creatives: IObservableArray<DisplayCreative>;
  poof: IUsePoofResult;
}

function AdWizardCreativesStepV2({ campaign, creatives, poof }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const [isCreativeModalOpen, toggleCreativeModal] = useToggle(false);
  const [selectedCreative, setSelectedCreative] = useState<DisplayCreative | undefined>();

  const { removingIds, appearingIds, animateRemove, animateAppear } = poof;

  const { duplicateCreative } = useDuplicateCreative({
    campaignId: campaign.id,
    eventId: campaign.get('event'),
    creatives,
    onAppear: animateAppear,
  });

  const { removeCreative } = useRemoveCreative({
    creatives,
    onAnimateRemove: animateRemove,
  });

  const thumbnailItems: IThumbnailCarouselItem[] = creatives.map((c) => ({
    id: c.id,
    thumbnailUrl: c.get('url'),
    filename: c.get('url')?.split('/').pop() ?? '',
    isVideo: c.creativeType === 'Video',
  }));

  function handleSelectCreative(id: string): void {
    const creative = creatives.find((c) => c.id === id);
    setSelectedCreative(creative);
  }

  function handleEditCreative(creative: DisplayCreative): void {
    setSelectedCreative(creative);
    toggleCreativeModal();
  }

  function handleDuplicateCreative(creative: DisplayCreative): void {
    duplicateCreative(creative.id, `${creative.name} (copy)`);
  }

  function handleDeleteCreative(creative: DisplayCreative): void {
    removeCreative(creative);
  }

  function getMenuItems(creative: DisplayCreative): ICreativeCardMenuItem[] {
    return [
      {
        label: t('Edit'),
        onClick: (): void => handleEditCreative(creative),
        prefix: faPencil,
      },
      {
        label: t('Duplicate'),
        onClick: (): void => handleDuplicateCreative(creative),
        prefix: faClone,
      },
      {
        label: t('Delete'),
        onClick: (): void => handleDeleteCreative(creative),
        prefix: faTrashCan,
        theme: 'danger',
      },
    ];
  }

  return (
    <>
      <Flex className={styles.root} justify={'space-between'}>
        <div className={styles.left}>
          <CreativesGrid
            creatives={creatives}
            emptyStateAction={
              <AddCreativeButton
                campaignId={campaign.id}
                creatives={creatives}
                eventId={campaign.get('event')}
                onAppear={animateAppear}
                type={'primary'}
              />
            }
            renderItem={(creative): JSX.Element => (
              <PoofGridItem
                id={creative.id}
                isAppearing={appearingIds.has(creative.id)}
                isRemoving={removingIds.has(creative.id)}
                key={creative.id}
              >
                <CreativeCard
                  adTagHtml={creative.get('adtag')}
                  channel={creative.isFacebook ? 'meta' : 'display'}
                  data-name={'creative-card'}
                  filename={creative.get('url')?.split('/').pop() ?? ''}
                  isEnabled={creative.get('is_enabled')}
                  menuItems={getMenuItems(creative)}
                  name={creative.name}
                  onEnabledChange={(enabled): void => {
                    runInAction(() => creative.set({ is_enabled: enabled }));
                  }}
                  onImageClick={(): void => handleEditCreative(creative)}
                  size={creative.dimensions}
                  status={'ready'}
                  thumbnailUrl={creative.get('url')}
                  type={creative.creativeType}
                />
              </PoofGridItem>
            )}
          />
          <CreativeDefaultSettings campaign={campaign} />
        </div>
        <div className={styles.right}>
          <CreativeRequirementsCard campaign={campaign} />
        </div>
      </Flex>
      <CreativeModal
        creative={selectedCreative}
        onClose={toggleCreativeModal}
        onSelectCreative={handleSelectCreative}
        opened={isCreativeModalOpen}
        thumbnailItems={thumbnailItems}
      />
    </>
  );
}

export default observer(AdWizardCreativesStepV2);
